import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { BookOpen, Download, Search, ArrowRight, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Section } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ReleaseList } from "../components/WhatsNew";
import { RELEASES } from "../lib/whatsNew";
import { resetTips } from "../lib/tour";
import { MANUAL, searchManual } from "../lib/manual";
import { manualPDF } from "../lib/manualPdf";

function Chapter({ c, n }) {
  return (
    <div id={`manual-${c.id}`} className="scroll-mt-20 md:scroll-mt-24">
    <Section title={`${n}. ${c.title}`} testid={`manual-chapter-${c.id}`} action={c.route && <Link to={c.route} className="text-xs text-primary inline-flex items-center gap-1" data-testid={`manual-open-${c.id}`}>Open screen <ArrowRight className="h-3 w-3" /></Link>}>
      <p className="text-sm text-muted-foreground mb-4">{c.intro}</p>
      <dl className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        {c.topics.map((t) => (
          <div key={t.h} data-testid={`manual-topic-${c.id}-${t.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <dt className="font-semibold">{t.h}</dt>
            {t.p.map((p) => <dd key={p} className="text-muted-foreground mt-1 leading-relaxed">{p}</dd>)}
          </div>
        ))}
      </dl>
    </Section>
    </div>
  );
}

export default function Help() {
  const [params] = useSearchParams();
  const topic = params.get("topic");
  const [q, setQ] = useState("");
  const [news, setNews] = useState(false);
  const [active, setActive] = useState(topic);
  const list = searchManual(q);
  useEffect(() => {
    setActive(topic);
    if (topic) setTimeout(() => document.getElementById(`manual-${topic}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [topic]);
  const jump = (id) => { setQ(""); setActive(id); setTimeout(() => document.getElementById(`manual-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 20); };
  return (
    <div data-testid="help-page">
      <PageHeader title="User manual" subtitle="How every part of Household Budget works, in short explanations. Tap ? next to any page title to jump here.">
        <Button variant="outline" onClick={() => setNews(true)} data-testid="manual-whats-new"><Sparkles className="h-4 w-4 mr-1" /> What's new</Button>
        <Button variant="outline" onClick={() => { resetTips(); toast.success("Tips will show again on each screen"); }} data-testid="manual-replay-tips"><Lightbulb className="h-4 w-4 mr-1" /> Replay screen tips</Button>
        <Button variant="outline" onClick={manualPDF} data-testid="manual-download-pdf"><Download className="h-4 w-4 mr-1" /> Download as PDF</Button>
      </PageHeader>
      <Dialog open={news} onOpenChange={setNews}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="manual-whats-new-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> What's new</DialogTitle></DialogHeader>
          <ReleaseList releases={RELEASES} />
        </DialogContent>
      </Dialog>
      <div className="grid lg:grid-cols-[220px_1fr] gap-6 items-start">
        <nav className="lg:sticky lg:top-24 rounded-xl border bg-card p-3" data-testid="manual-contents">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> Contents</p>
          <ol className="flex flex-wrap lg:flex-col gap-1 text-sm">
            {MANUAL.map((c, i) => (
              <li key={c.id}><button onClick={() => jump(c.id)} className={`text-left rounded-md px-2 py-1 hover:bg-secondary ${active === c.id ? "text-primary font-medium" : ""}`} data-testid={`manual-nav-${c.id}`}>{i + 1}. {c.title}</button></li>
            ))}
          </ol>
        </nav>
        <div className="space-y-6 min-w-0 pb-24 md:pb-0">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the manual (e.g. backup, receipts, sync)" className="pl-9" data-testid="manual-search-input" />
          </div>
          {q && <p className="text-xs text-muted-foreground" data-testid="manual-search-count">{list.reduce((s, c) => s + c.topics.length, 0)} matching topic(s)</p>}
          {list.length ? list.map((c) => <Chapter key={c.id} c={c} n={MANUAL.findIndex((m) => m.id === c.id) + 1} />) : <p className="text-sm text-muted-foreground" data-testid="manual-search-empty">Nothing in the manual matches “{q}”.</p>}
        </div>
      </div>
    </div>
  );
}
